import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * live-shopping-style tap reactions for live streams.
 *
 *  - Tap anywhere on the overlay → emoji floats up at the tap point
 *  - Currently selected emoji shown in a picker bar at bottom-right
 *  - Tap broadcasts via the existing stream WebSocket so everyone sees it
 *  - Local taps render optimistically for zero-latency feedback; remote taps
 *    coming back over WS that match our own conn_id are filtered out so we
 *    don't double-render
 *  - Hard cap of 60 simultaneously rendered emoji to keep DOM sane on viral
 *    moments (a "tap storm")
 *  - Client-side throttle: 10 taps per 1000ms (server enforces 30/sec
 *    backstop)
 *
 * Props:
 *  - wsRef: React ref to the active stream WebSocket
 *  - myConnIdRef: React ref to the local conn_id (from `welcome` frame)
 *  - tapCount: live tap count to display (running total from stream doc)
 *  - onLocalTap?: (emoji) => void  fires for every local tap (for UI badges)
 */
const EMOJI_SET = ["❤️", "🔥", "👏", "😂", "💎", "🙌"];
const MAX_RENDERED = 60;
const LOCAL_RATE_MS = 100; // 10/s
const LIFETIME_MS = 2700;
// Double-tap window: a second tap within this window at roughly the same
// (fractional) location → trigger a heart burst (live-shopping double-tap pattern).
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_DIST = 0.15; // 15% of the layer's width/height
const HEART_BURST_COUNT = 7;

export default function LiveTapLayer({ wsRef, myConnIdRef, tapCount = 0, onLocalTap }) {
  const [activeEmoji, setActiveEmoji] = useState("❤️");
  const [bursts, setBursts] = useState([]);
  const lastSentRef = useRef(0);
  const lastTapRef = useRef({ t: 0, x: 0, y: 0 });
  const layerRef = useRef(null);

  // Render an emoji burst at fractional (x, y) inside the layer.
  const spawn = useCallback((emoji, x, y, opts = {}) => {
    const id = `b_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    // Randomize horizontal sway and rotation so each emoji feels unique.
    const dxMid = (Math.random() - 0.5) * 80;     // -40..40
    const dxEnd = dxMid + (Math.random() - 0.5) * 60;
    const rot0 = (Math.random() - 0.5) * 20;
    const rot1 = rot0 + (Math.random() - 0.5) * 40;
    const rot2 = rot1 + (Math.random() - 0.5) * 30;
    const baseSize = opts.big ? 56 : 30;
    const variance = opts.big ? 22 : 16;
    const size = baseSize + Math.random() * variance;
    setBursts((prev) => {
      const next = [
        ...prev,
        {
          id,
          emoji,
          x,
          y,
          dxMid,
          dxEnd,
          rot0,
          rot1,
          rot2,
          size,
        },
      ];
      // Cap concurrent emojis — drop the oldest.
      if (next.length > MAX_RENDERED) next.splice(0, next.length - MAX_RENDERED);
      return next;
    });
    setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b.id !== id));
    }, LIFETIME_MS);
  }, []);

  // Spawn a multi-heart explosion at (x, y). Used for double-tap reactions
  // and remote double-tap echoes.
  const spawnHeartBurst = useCallback(
    (x, y) => {
      for (let i = 0; i < HEART_BURST_COUNT; i++) {
        const jx = Math.max(0, Math.min(1, x + (Math.random() - 0.5) * 0.1));
        const jy = Math.max(0, Math.min(1, y + (Math.random() - 0.5) * 0.06));
        // Stagger so they explode outward instead of stacking.
        setTimeout(() => spawn("❤️", jx, jy, { big: true }), i * 35);
      }
    },
    [spawn]
  );

  const handleTap = useCallback(
    (e) => {
      const layer = layerRef.current;
      if (!layer) return;
      const rect = layer.getBoundingClientRect();
      const point = e.touches?.[0] || e.changedTouches?.[0] || e;
      const px = (point.clientX ?? rect.left + rect.width / 2) - rect.left;
      const py = (point.clientY ?? rect.top + rect.height / 2) - rect.top;
      const x = Math.max(0, Math.min(1, px / rect.width));
      const y = Math.max(0, Math.min(1, py / rect.height));

      // Optimistic local render — instant feedback.
      spawn(activeEmoji, x, y);
      onLocalTap?.(activeEmoji);

      // Double-tap detection — live-shopping double-tap pattern. Two taps inside
      // ~320ms within ~15% of each other → trigger a heart burst on top of
      // the single-tap that already fired. We also broadcast a `double=true`
      // tap so every other viewer sees the explosion.
      const nowTs = performance.now();
      const last = lastTapRef.current;
      const dt = nowTs - last.t;
      const dx = Math.abs(x - last.x);
      const dy = Math.abs(y - last.y);
      const isDoubleTap =
        dt < DOUBLE_TAP_MS && dx < DOUBLE_TAP_DIST && dy < DOUBLE_TAP_DIST;

      if (isDoubleTap) {
        // Reset so a third quick tap doesn't chain into another burst.
        lastTapRef.current = { t: 0, x: 0, y: 0 };
        spawnHeartBurst(x, y);
        // Subtle haptic (mobile only — desktop just ignores).
        try {
          navigator.vibrate?.(30);
        } catch {
          /* noop */
        }
        const ws = wsRef?.current;
        if (ws && ws.readyState === 1) {
          try {
            ws.send(
              JSON.stringify({ type: "tap", emoji: "❤️", x, y, double: true })
            );
          } catch {
            /* fail silently */
          }
        }
        // Skip the throttled single-tap broadcast so we don't double-count.
        return;
      }

      lastTapRef.current = { t: nowTs, x, y };

      // Throttled WS broadcast (single tap).
      const now = performance.now();
      if (now - lastSentRef.current < LOCAL_RATE_MS) return;
      lastSentRef.current = now;
      const ws = wsRef?.current;
      if (ws && ws.readyState === 1) {
        try {
          ws.send(JSON.stringify({ type: "tap", emoji: activeEmoji, x, y }));
        } catch {
          /* fail silently */
        }
      }
    },
    [activeEmoji, spawn, spawnHeartBurst, wsRef, onLocalTap]
  );

  // Subscribe to incoming taps coming back from the WS broadcast.
  useEffect(() => {
    const ws = wsRef?.current;
    if (!ws) return;
    const onMsg = (evt) => {
      let data;
      try {
        data = JSON.parse(evt.data);
      } catch {
        return;
      }
      if (data?.type !== "tap") return;
      // Filter out echo of our own taps — we already rendered them locally.
      if (myConnIdRef?.current && data.from === myConnIdRef.current) return;
      const x = typeof data.x === "number" ? data.x : 0.5;
      const y = typeof data.y === "number" ? data.y : 0.5;
      if (data.double) {
        spawnHeartBurst(x, y);
      } else {
        spawn(data.emoji || "❤️", x, y);
      }
    };
    ws.addEventListener("message", onMsg);
    return () => ws.removeEventListener("message", onMsg);
  }, [wsRef, myConnIdRef, spawn, spawnHeartBurst]);

  // Allow the desktop right-rail reactions popover to drive the active
  // emoji. It dispatches `bidorah:emoji-pick` with `{ detail: { emoji } }`
  // when the user clicks one of the 6 emoji tiles. Mobile + desktop legacy
  // bottom picker still works exactly as before.
  useEffect(() => {
    const onPick = (e) => {
      const emoji = e?.detail?.emoji;
      if (typeof emoji === "string" && EMOJI_SET.includes(emoji)) {
        setActiveEmoji(emoji);
      }
    };
    window.addEventListener("bidorah:emoji-pick", onPick);
    return () => window.removeEventListener("bidorah:emoji-pick", onPick);
  }, []);

  return (
    <div
      ref={layerRef}
      className="tap-layer"
      onPointerDown={handleTap}
      data-testid="tap-layer"
    >
      {bursts.map((b) => (
        <span
          key={b.id}
          className="tap-emoji"
          aria-hidden="true"
          style={{
            left: `${b.x * 100}%`,
            top: `${b.y * 100}%`,
            fontSize: `${b.size}px`,
            ["--dx"]: `${b.dxMid}px`,
            ["--dx-end"]: `${b.dxEnd}px`,
            ["--rot-0"]: `${b.rot0}deg`,
            ["--rot-1"]: `${b.rot1}deg`,
            ["--rot-2"]: `${b.rot2}deg`,
          }}
        >
          {b.emoji}
        </span>
      ))}

      {/* Tap counter / emoji picker tray retired. The right-rail
          "Reactions" circle now drives the active emoji via the
          `bidorah:emoji-pick` window event, and we no longer show a
          tap-count pill at all (sellers asked to remove it — it was
          competing visually with the live show). */}
    </div>
  );
}

function formatCount(n) {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return (n / 1000).toFixed(n < 10_000 ? 1 : 0) + "k";
  return (n / 1_000_000).toFixed(1) + "M";
}
